import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, addDays } from "date-fns";
import { es } from "date-fns/locale";
import { MapPin, CalendarDays, Users, Search, ArrowLeftRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { useCities } from "../../lib/useCities";

const Field = ({ label, icon: Icon, children, className = "" }) => (
  <div className={`flex min-w-0 flex-col justify-center px-5 py-3 ${className}`}>
    <span className="mb-1 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted-ink)]">{label}</span>
    <div className="flex min-w-0 items-center gap-2.5"><Icon className="h-[18px] w-[18px] shrink-0 text-[var(--forest)]" />{children}</div>
  </div>
);

const CitySelect = ({ value, onChange, testid, exclude }) => {
  const cities = useCities();
  return (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="h-auto min-w-0 border-0 bg-transparent p-0 text-base font-semibold shadow-none focus:ring-0">
      <SelectValue placeholder="Elegí" />
    </SelectTrigger>
    <SelectContent>
      {cities.filter((c) => c !== exclude).map((c) => <SelectItem key={c} value={c} data-testid={`${testid}-option-${c}`}>{c}</SelectItem>)}
    </SelectContent>
  </Select>
  );
};

const DateField = ({ value, onChange, testid, min }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" data-testid={testid} className="min-w-0 truncate text-left text-base font-semibold">
        {value ? format(value, "EEE d MMM", { locale: es }) : <span className="text-[var(--muted-ink)]">Elegí una fecha</span>}
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-0" align="start">
      <Calendar mode="single" selected={value} onSelect={(d) => d && onChange(d)} locale={es} disabled={{ before: min }} initialFocus />
    </PopoverContent>
  </Popover>
);

export const SearchCard = ({ initial = {}, compact = false }) => {
  const nav = useNavigate();
  const today = new Date(new Date().setHours(0, 0, 0, 0));
  const [round, setRound] = useState(Boolean(initial.r));
  const [origin, setOrigin] = useState(initial.o || "Retiro");
  const [dest, setDest] = useState(initial.d || "Tucumán");
  const [date, setDate] = useState(initial.f ? new Date(`${initial.f}T00:00`) : addDays(today, 1));
  const [ret, setRet] = useState(initial.r ? new Date(`${initial.r}T00:00`) : addDays(today, 8));
  const [pax, setPax] = useState(String(initial.p || 1));
  const [spin, setSpin] = useState(0);

  const swap = () => { setOrigin(dest); setDest(origin); setSpin((s) => s + 180); };
  const submit = (e) => {
    e.preventDefault();
    const q = new URLSearchParams({ o: origin, d: dest, f: format(date, "yyyy-MM-dd"), p: pax });
    if (round) q.set("r", format(ret < date ? date : ret, "yyyy-MM-dd"));
    nav(`/pasajes?${q.toString()}`);
  };

  return (
    <form onSubmit={submit} data-testid="trip-search-form" className={`relative rounded-[28px] bg-white p-3 shadow-[0_30px_80px_-30px_rgba(11,17,13,.35)] ring-1 ring-black/5 ${compact ? "" : "md:p-4"}`}>
      <div className="mb-3 flex gap-2 px-1 pt-1">
        {[["Solo ida", false], ["Ida y vuelta", true]].map(([l, v]) => (
          <button key={l} type="button" onClick={() => setRound(v)} data-testid={`trip-type-${v ? "round" : "oneway"}`} className={`rounded-full px-5 py-2 text-sm font-bold transition-colors duration-300 ${round === v ? "bg-[var(--forest)] text-white" : "bg-[var(--paper)] text-[var(--ink)] hover:bg-[#e6e5dc]"}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
        <div className="relative grid flex-1 grid-cols-1 divide-y divide-black/10 rounded-2xl border border-black/10 sm:grid-cols-2 sm:divide-y-0 lg:flex lg:divide-x">
          <Field label="Origen" icon={MapPin} className="lg:flex-1"><CitySelect value={origin} onChange={setOrigin} testid="search-origin" exclude={dest} /></Field>
          <button type="button" onClick={swap} data-testid="search-swap" aria-label="Invertir origen y destino" className="absolute right-4 top-[58px] z-10 grid h-9 w-9 place-items-center rounded-full border border-black/10 bg-white transition-colors hover:bg-[var(--lime)] sm:left-1/2 sm:right-auto sm:top-[22px] sm:-translate-x-1/2 lg:hidden">
            <ArrowLeftRight className="h-4 w-4 transition-transform duration-500 sm:rotate-0" style={{ transform: `rotate(${spin}deg)` }} />
          </button>
          <Field label="Destino" icon={MapPin} className="sm:border-l sm:border-black/10 lg:flex-1 lg:border-l-0"><CitySelect value={dest} onChange={setDest} testid="search-destination" exclude={origin} /></Field>
          <Field label="Salida" icon={CalendarDays} className="sm:border-t sm:border-black/10 lg:flex-1 lg:border-t-0"><DateField value={date} onChange={setDate} testid="search-date" min={today} /></Field>
          {round && <Field label="Regreso" icon={CalendarDays} className="sm:border-l sm:border-t sm:border-black/10 lg:flex-1 lg:border-l-0 lg:border-t-0"><DateField value={ret} onChange={setRet} testid="search-return-date" min={date} /></Field>}
          <Field label="Pasajeros" icon={Users} className={`sm:border-t sm:border-black/10 lg:w-44 lg:border-t-0 ${round ? "" : "sm:border-l lg:border-l-0"}`}>
            <Select value={pax} onValueChange={setPax}>
              <SelectTrigger data-testid="search-passengers" className="h-auto border-0 bg-transparent p-0 text-base font-semibold shadow-none focus:ring-0"><SelectValue /></SelectTrigger>
              <SelectContent>{[1, 2, 3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>{n} {n === 1 ? "adulto" : "adultos"}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <button type="submit" data-testid="search-submit-button" className="group flex h-16 items-center justify-center gap-3 rounded-2xl bg-[var(--forest)] px-8 text-base font-bold text-white transition-[background-color,transform] duration-300 hover:bg-[var(--forest-2)] active:scale-[0.98] lg:h-auto lg:min-w-[220px]">
          <Search className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" /> Buscar pasajes
        </button>
      </div>
    </form>
  );
};
